import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Button,
	Card,
	Col,
	DatePicker,
	Input,
	InputNumber,
	Popconfirm,
	Row,
	Select,
	Space,
	Spin,
	Tag,
	Typography,
	message,
} from "antd";
import {
	ArrowLeftOutlined,
	PlusOutlined,
	SaveOutlined,
	DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
	parseKoreaDateTime,
	toKoreaApiDateTime,
} from "../../utilities/koreaTime";
import useAxios from "../../utilities/useAxios";
import PageTitle from "../PageTitle";
import useIsMobile from "../../hooks/useIsMobile";

const { Title, Text } = Typography;
const { TextArea } = Input;

const DEFAULT_EXAM = {
	title: "과정 시험",
	start_at: null,
	end_at: null,
	pass_score: 80,
};

const DEFAULT_QUESTION = {
	question_type: "MULTIPLE_CHOICE",
	question_text: "",
	order: 1,
	score: 10,
	choices_text: "",
	correct_choice_index: 0,
	correct_answers_text: "",
};

const AdminCourseExamPage = () => {
	const isMobile = useIsMobile();
	const { courseId } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [savingExam, setSavingExam] = useState(false);
	const [savingQuestion, setSavingQuestion] = useState(false);
	const [data, setData] = useState(null);
	const [examForm, setExamForm] = useState(DEFAULT_EXAM);
	const [questions, setQuestions] = useState([]);
	const [newQuestion, setNewQuestion] = useState(DEFAULT_QUESTION);

	const hasExam = !!data?.has_exam;
	const editable = !!data?.editable;
	const examId = data?.exam?.id;

	const courseTitle = useMemo(() => {
		return data?.course_name || courseId;
	}, [courseId, data]);

	const parseChoices = (text) =>
		(text || "")
			.split("\n")
			.map((v) => v.trim())
			.filter((v) => v.length > 0);

	const parseAnswers = (text) =>
		(text || "")
			.split("\n")
			.map((v) => v.trim())
			.filter((v) => v.length > 0);

	const toQuestionEditor = (q) => ({
		...q,
		choices_text: (q.choices || []).join("\n"),
		correct_answers_text: (q.correct_answers || []).join("\n"),
	});

	const fetchExam = useCallback(async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				`/cma/admin/courses/${courseId}/exam/`,
			);
			setData(response.data);
			if (response.data?.has_exam) {
				const exam = response.data.exam;
				setExamForm({
					title: exam.title,
					start_at: parseKoreaDateTime(exam.start_at),
					end_at: parseKoreaDateTime(exam.end_at),
					pass_score: exam.pass_score,
				});
				setQuestions(
					(exam.questions || []).map((q) =>
						toQuestionEditor(q),
					),
				);
			} else {
				setExamForm(DEFAULT_EXAM);
				setQuestions([]);
			}
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"시험 정보를 불러오는데 실패했습니다.",
			);
		} finally {
			setLoading(false);
		}
	}, [courseId]);

	useEffect(() => {
		fetchExam();
	}, [fetchExam]);

	const handleSaveExam = async () => {
		try {
			setSavingExam(true);
			const payload = {
				title: examForm.title,
				start_at: toKoreaApiDateTime(examForm.start_at),
				end_at: toKoreaApiDateTime(examForm.end_at),
				pass_score: examForm.pass_score,
			};
			if (!payload.start_at || !payload.end_at) {
				message.error(
					"시험 시작/종료 일시를 입력해주세요.",
				);
				return;
			}

			if (hasExam) {
				await useAxios.put(
					`/cma/admin/courses/${courseId}/exam/`,
					payload,
				);
				message.success("시험 정보가 수정되었습니다.");
			} else {
				await useAxios.post(
					`/cma/admin/courses/${courseId}/exam/`,
					payload,
				);
				message.success("시험이 생성되었습니다.");
			}
			await fetchExam();
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"시험 저장에 실패했습니다.",
			);
		} finally {
			setSavingExam(false);
		}
	};

	const buildQuestionPayload = (q) => {
		const payload = {
			question_type: q.question_type,
			question_text: q.question_text,
			order: q.order,
			score: q.score,
			choices: [],
			correct_choice_index: null,
			correct_answers: [],
		};
		if (q.question_type === "MULTIPLE_CHOICE") {
			payload.choices = parseChoices(q.choices_text);
			payload.correct_choice_index = q.correct_choice_index;
		} else {
			payload.correct_answers = parseAnswers(
				q.correct_answers_text,
			);
		}
		return payload;
	};

	const handleCreateQuestion = async () => {
		if (!examId) return;
		try {
			setSavingQuestion(true);
			await useAxios.post(
				`/cma/admin/exams/${examId}/questions/`,
				buildQuestionPayload(newQuestion),
			);
			message.success("문항이 추가되었습니다.");
			setNewQuestion(DEFAULT_QUESTION);
			await fetchExam();
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"문항 추가에 실패했습니다.",
			);
		} finally {
			setSavingQuestion(false);
		}
	};

	const handleUpdateQuestion = async (question) => {
		if (!examId) return;
		try {
			setSavingQuestion(true);
			await useAxios.put(
				`/cma/admin/exams/${examId}/questions/${question.id}/`,
				buildQuestionPayload(question),
			);
			message.success("문항이 수정되었습니다.");
			await fetchExam();
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"문항 수정에 실패했습니다.",
			);
		} finally {
			setSavingQuestion(false);
		}
	};

	const handleDeleteQuestion = async (questionId) => {
		if (!examId) return;
		try {
			await useAxios.delete(
				`/cma/admin/exams/${examId}/questions/${questionId}/`,
			);
			message.success("문항이 삭제되었습니다.");
			await fetchExam();
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"문항 삭제에 실패했습니다.",
			);
		}
	};

	const updateExistingQuestion = (id, patch) => {
		setQuestions((prev) =>
			prev.map((q) => (q.id === id ? { ...q, ...patch } : q)),
		);
	};

	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: "50px" }}>
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div style={{ padding: "24px" }}>
			<PageTitle title="기독사관학교 - 시험 관리" />
			<Card style={{ marginBottom: "16px" }}>
				<Space
					style={{
						width: "100%",
						justifyContent: "space-between",
					}}>
					<div>
						<Title
							level={3}
							style={{ margin: 0 }}>
							시험 관리
						</Title>
						<Text type="secondary">
							강의 ID: {courseTitle}
						</Text>
					</div>
					<Button
						icon={<ArrowLeftOutlined />}
						onClick={() =>
							navigate("/cma/admin")
						}>
						관리자 페이지
					</Button>
				</Space>
			</Card>

			{hasExam && !editable && (
				<Alert
					type="warning"
					showIcon
					style={{ marginBottom: "16px" }}
					message="시험이 시작되어 더 이상 수정할 수 없습니다."
				/>
			)}

			<Card title={hasExam ? "시험 정보 수정" : "시험 생성"}>
				<Row gutter={[12, 12]}>
					<Col span={24}>
						<Text>시험명</Text>
						<Input
							value={examForm.title}
							disabled={
								hasExam &&
								!editable
							}
							onChange={(e) =>
								setExamForm(
									(
										prev,
									) => ({
										...prev,
										title: e
											.target
											.value,
									}),
								)
							}
						/>
					</Col>
					<Col span={8}>
						<Text>시험 시작 일시</Text>
						<DatePicker
							inputReadOnly={isMobile}
							showTime={{
								format: "HH:mm",
							}}
							format="YYYY-MM-DD HH:mm"
							style={{
								width: "100%",
							}}
							value={
								examForm.start_at
							}
							disabled={
								hasExam &&
								!editable
							}
							onChange={(v) =>
								setExamForm(
									(
										prev,
									) => ({
										...prev,
										start_at: v,
									}),
								)
							}
						/>
					</Col>
					<Col span={8}>
						<Text>시험 종료 일시</Text>
						<DatePicker
							inputReadOnly={isMobile}
							showTime={{
								format: "HH:mm",
							}}
							format="YYYY-MM-DD HH:mm"
							style={{
								width: "100%",
							}}
							value={examForm.end_at}
							disabled={
								hasExam &&
								!editable
							}
							onChange={(v) =>
								setExamForm(
									(
										prev,
									) => ({
										...prev,
										end_at: v,
									}),
								)
							}
						/>
					</Col>
					<Col span={8}>
						<Text>통과 점수</Text>
						<InputNumber
							style={{
								width: "100%",
							}}
							min={0}
							max={1000}
							value={
								examForm.pass_score
							}
							disabled={
								hasExam &&
								!editable
							}
							onChange={(v) =>
								setExamForm(
									(
										prev,
									) => ({
										...prev,
										pass_score:
											v ??
											80,
									}),
								)
							}
						/>
					</Col>
				</Row>
				<Button
					type="primary"
					icon={<SaveOutlined />}
					style={{ marginTop: "16px" }}
					onClick={handleSaveExam}
					loading={savingExam}
					disabled={hasExam && !editable}>
					{hasExam
						? "시험 정보 저장"
						: "시험 생성"}
				</Button>
			</Card>

			{hasExam && (
				<Card
					title={
						<Space>
							시험 문항
							<Tag color="blue">
								{
									questions.length
								}
								개
							</Tag>
						</Space>
					}
					style={{ marginTop: "16px" }}>
					<Space
						direction="vertical"
						style={{ width: "100%" }}
						size={16}>
						{questions.map((q) => (
							<Card
								key={q.id}
								size="small"
								title={`문항 #${q.order}`}>
								<Row
									gutter={[
										12,
										12,
									]}>
									<Col
										span={
											6
										}>
										<Text>
											유형
										</Text>
										<Select
											style={{
												width: "100%",
											}}
											value={
												q.question_type
											}
											disabled={
												!editable
											}
											onChange={(
												v,
											) =>
												updateExistingQuestion(
													q.id,
													{
														question_type:
															v,
													},
												)
											}
											options={[
												{
													value: "MULTIPLE_CHOICE",
													label: "객관식",
												},
												{
													value: "SHORT_ANSWER",
													label: "단답형",
												},
											]}
										/>
									</Col>
									<Col
										span={
											6
										}>
										<Text>
											순서
										</Text>
										<InputNumber
											style={{
												width: "100%",
											}}
											value={
												q.order
											} // 기존 문제 개수 + 1
											disabled={
												!editable
											}
											onChange={(
												v,
											) =>
												updateExistingQuestion(
													q.id,
													{
														order:
															v ??
															1,
													},
												)
											}
										/>
									</Col>
									<Col
										span={
											6
										}>
										<Text>
											점수
										</Text>
										<InputNumber
											style={{
												width: "100%",
											}}
											value={
												q.score
											}
											disabled={
												!editable
											}
											onChange={(
												v,
											) =>
												updateExistingQuestion(
													q.id,
													{
														score:
															v ??
															10,
													},
												)
											}
										/>
									</Col>
								</Row>
								<div
									style={{
										marginTop: "12px",
									}}>
									<Text>
										문제
									</Text>
									<TextArea
										rows={
											2
										}
										value={
											q.question_text
										}
										disabled={
											!editable
										}
										onChange={(
											e,
										) =>
											updateExistingQuestion(
												q.id,
												{
													question_text:
														e
															.target
															.value,
												},
											)
										}
									/>
								</div>
								{q.question_type ===
								"MULTIPLE_CHOICE" ? (
									<>
										<div
											style={{
												marginTop: "12px",
											}}>
											<Text>
												선택지
												(줄바꿈으로
												구분)
											</Text>
											<TextArea
												rows={
													3
												}
												value={
													q.choices_text
												}
												disabled={
													!editable
												}
												onChange={(
													e,
												) =>
													updateExistingQuestion(
														q.id,
														{
															choices_text:
																e
																	.target
																	.value,
														},
													)
												}
											/>
										</div>
										<div
											style={{
												marginTop: "12px",
											}}>
											<Text>
												정답
												인덱스
												(0부터)
											</Text>
											<InputNumber
												style={{
													width: "100%",
												}}
												value={
													q.correct_choice_index
												}
												disabled={
													!editable
												}
												onChange={(
													v,
												) =>
													updateExistingQuestion(
														q.id,
														{
															correct_choice_index:
																v ??
																0,
														},
													)
												}
											/>
										</div>
									</>
								) : (
									<div
										style={{
											marginTop: "12px",
										}}>
										<Text>
											정답
											(줄바꿈으로
											복수
											정답)
										</Text>
										<TextArea
											rows={
												3
											}
											value={
												q.correct_answers_text
											}
											disabled={
												!editable
											}
											onChange={(
												e,
											) =>
												updateExistingQuestion(
													q.id,
													{
														correct_answers_text:
															e
																.target
																.value,
													},
												)
											}
										/>
									</div>
								)}
								<Space
									style={{
										marginTop: "12px",
									}}>
									<Button
										type="primary"
										icon={
											<SaveOutlined />
										}
										onClick={() =>
											handleUpdateQuestion(
												q,
											)
										}
										loading={
											savingQuestion
										}
										disabled={
											!editable
										}>
										문항
										저장
									</Button>
									<Popconfirm
										title="문항을 삭제할까요?"
										onConfirm={() =>
											handleDeleteQuestion(
												q.id,
											)
										}
										okText="삭제"
										cancelText="취소"
										disabled={
											!editable
										}>
										<Button
											danger
											icon={
												<DeleteOutlined />
											}
											disabled={
												!editable
											}>
											문항
											삭제
										</Button>
									</Popconfirm>
								</Space>
							</Card>
						))}

						<Card
							size="small"
							title="새 문항 추가">
							<Row gutter={[12, 12]}>
								<Col span={6}>
									<Text>
										유형
									</Text>
									<Select
										style={{
											width: "100%",
										}}
										value={
											newQuestion.question_type
										}
										disabled={
											!editable
										}
										onChange={(
											v,
										) =>
											setNewQuestion(
												(
													prev,
												) => ({
													...prev,
													question_type:
														v,
												}),
											)
										}
										options={[
											{
												value: "MULTIPLE_CHOICE",
												label: "객관식",
											},
											{
												value: "SHORT_ANSWER",
												label: "단답형",
											},
										]}
									/>
								</Col>
								<Col span={6}>
									<Text>
										순서
									</Text>
									<InputNumber
										style={{
											width: "100%",
										}}
										value={
											questions.length +
											1
										}
										disabled={
											!editable
										}
										onChange={(
											v,
										) =>
											setNewQuestion(
												(
													prev,
												) => ({
													...prev,
													order:
														v ??
														questions.length +
															1,
												}),
											)
										}
									/>
								</Col>
								<Col span={6}>
									<Text>
										점수
									</Text>
									<InputNumber
										style={{
											width: "100%",
										}}
										value={
											newQuestion.score
										}
										disabled={
											!editable
										}
										onChange={(
											v,
										) =>
											setNewQuestion(
												(
													prev,
												) => ({
													...prev,
													score:
														v ??
														10,
												}),
											)
										}
									/>
								</Col>
							</Row>
							<div
								style={{
									marginTop: "12px",
								}}>
								<Text>
									문제
								</Text>
								<TextArea
									rows={2}
									value={
										newQuestion.question_text
									}
									disabled={
										!editable
									}
									onChange={(
										e,
									) =>
										setNewQuestion(
											(
												prev,
											) => ({
												...prev,
												question_text:
													e
														.target
														.value,
											}),
										)
									}
								/>
							</div>
							{newQuestion.question_type ===
							"MULTIPLE_CHOICE" ? (
								<>
									<div
										style={{
											marginTop: "12px",
										}}>
										<Text>
											선택지
											(줄바꿈으로
											구분)
										</Text>
										<TextArea
											rows={
												3
											}
											value={
												newQuestion.choices_text
											}
											disabled={
												!editable
											}
											onChange={(
												e,
											) =>
												setNewQuestion(
													(
														prev,
													) => ({
														...prev,
														choices_text:
															e
																.target
																.value,
													}),
												)
											}
										/>
									</div>
									<div
										style={{
											marginTop: "12px",
										}}>
										<Text>
											정답
											인덱스
											(0부터)
										</Text>
										<InputNumber
											style={{
												width: "100%",
											}}
											value={
												newQuestion.correct_choice_index
											}
											disabled={
												!editable
											}
											onChange={(
												v,
											) =>
												setNewQuestion(
													(
														prev,
													) => ({
														...prev,
														correct_choice_index:
															v ??
															0,
													}),
												)
											}
										/>
									</div>
								</>
							) : (
								<div
									style={{
										marginTop: "12px",
									}}>
									<Text>
										정답
										(줄바꿈으로
										복수
										정답)
									</Text>
									<TextArea
										rows={
											3
										}
										value={
											newQuestion.correct_answers_text
										}
										disabled={
											!editable
										}
										onChange={(
											e,
										) =>
											setNewQuestion(
												(
													prev,
												) => ({
													...prev,
													correct_answers_text:
														e
															.target
															.value,
												}),
											)
										}
									/>
								</div>
							)}
							<Button
								type="dashed"
								icon={
									<PlusOutlined />
								}
								style={{
									marginTop: "12px",
								}}
								disabled={
									!editable
								}
								loading={
									savingQuestion
								}
								onClick={
									handleCreateQuestion
								}>
								문항 추가
							</Button>
						</Card>
					</Space>
				</Card>
			)}
		</div>
	);
};

export default AdminCourseExamPage;
